import React, { useState, useEffect } from 'react';
import { Accordion, AccordionHeader, AccordionBody, AccordionItem, Table } from 'react-bootstrap';
import { toUSD } from '../../util/currency-formatter';
import PreviewBudgetTable from 'src/routes/projects/project-execution/budget/preview-budget-table';
import idx from 'idx';
import { round } from 'lodash';
import { roundValue } from 'src/util/decimal-utils';
import AssignmentTurnedInIcon from '@mui/icons-material/AssignmentTurnedIn';

/**
 *  Updated to support section-level claims with is_attribution_level
 * When a section has is_attribution_level=true:
 * - Show a single claimable row at the section level
 * - Hide child tasks/subsections
 * - Allow claim entry on the section row itself
 */
function StageAccordionGenerateInvoice({
    stage,
    optionData,
    stageSummaries,
    renderRowData,
    renderSectionRowData, // New prop for section-level claim rows
    selectedTask = [],
    isHistoricalView,
    onSectionClaimChange, // Handler for section-level claim changes
    progressClaims = [], // For calculating previous claims
    progressClaimId // Current claim ID for edit mode
}) {
    const [open, setOpen] = useState(false);
    let variationData = [];

    // Check if this section has attribution level set (replaces lock_flag)
    const isAttributionLevel = stage.is_attribution_level === true || stage.lock_flag === true;

    // Visibility and expansion flags
    // Attributed sections CAN expand to show the section claim row
    const canExpand = true; // All sections can expand
    const shouldShowSectionClaimRow = isAttributionLevel;

    // Determine if we should show nested stages
    const hasNestedStages = !isAttributionLevel && Array.isArray(stage.stages) && stage.stages.length > 0;

    if (optionData && Array.isArray(optionData) && optionData.length) {
        variationData = optionData.filter((option) => !["REJECTED", "CANCELLED"].includes(option.proposal_status) && option.stageId === stage.id);
    }

    const { totalCost = 0, overallClaimPercentage = 0, totalClaimedAmount = 0, stillToClaimPercentage = 0 } = idx(stageSummaries[stage.id], _ => _) || {};

    // Handle accordion toggle
    const handleAccordionToggle = () => {
        setOpen(!open);
    };


    return (
        <Accordion
            key={stage.id}
            activeKey={open ? stage.id : null}
            onSelect={handleAccordionToggle}
            className={`
                ${stage.parent_stage_id ? "p-0" : ""} 
                ${isAttributionLevel ? 'reporting-root attribution-level' : 'non-reporting-section'} 
            `}
        >
            <AccordionItem eventKey={stage.id}>
                <AccordionHeader
                    className={`accordion-icon border projectableContainer justify-content-between 
                        ${isAttributionLevel ? 'locked-section attribution-section' : ''}
                        ${stage.parent_stage_id ? 'mh-48' : ''} 
                    `}
                    data-cy={isAttributionLevel ? 'attribution-invoice-section' : 'invoice-section'}
                >
                    <div className="d-flex w-100 align-items-center">
                        {/* Attribution icon replaces or accompanies accordion arrow */}
                        {isAttributionLevel && (
                            <span className="invoice-attribution-icon me-2" title="Section-level claim attribution">
                                <AssignmentTurnedInIcon fontSize="small" color="primary" />
                            </span>
                        )}
                        <span className="brand-color-3 acc-header-text text-uppercase text-truncate me-auto d-flex align-items-center gap-2">
                            {stage.name}
                        </span>
                        <div className="d-flex flex-grow-1 justify-content-end gap-2">
                            <span className="total-color text-right d-flex flex-column min-w-100">
                                {!stage.parent_stage_id ? <span className="fst-12">Cost:</span> : ''}
                                <span>{toUSD(totalCost)}</span>
                            </span>
                            <span className="total-color text-right d-flex flex-column min-w-100">
                                {!stage.parent_stage_id ? <span className="fst-12">OverAll Claim(%):</span> : ''}
                                <span>
                                    {
                                        /* Ensures very small negative or floating-point values like -0.00001 are displayed as "0.00" */
                                        Math.abs(overallClaimPercentage) < 0.01 ? '0.00' : overallClaimPercentage.toFixed(2)
                                    }%
                                </span>
                            </span>

                            <span className="total-color text-right d-flex flex-column min-w-120">
                                {!stage.parent_stage_id ? <span className="fst-12">Claimed Amount:</span> : ''}
                                <span>
                                    {toUSD(totalClaimedAmount)}
                                </span>
                            </span>
                            <span className="total-color text-right d-flex flex-column min-w-100 me-2">
                                {!stage.parent_stage_id ? <span className="fst-12">Still to Claim(%):</span> : ''}
                                <span>
                                    {
                                        /* Ensures very small negative or floating-point values like -0.00001 are displayed as "0.00" */
                                        Math.abs(stillToClaimPercentage) < 0.01 ? '0.00' : stillToClaimPercentage.toFixed(2)
                                    }%
                                </span>
                            </span>
                        </div>
                    </div>
                </AccordionHeader>

                <AccordionBody className={`${!hasNestedStages && !isAttributionLevel ? "p-0" : stage.stages ? "p-0 ps-2" : "p-0"}`}>

                    {/* Render section claim row for attributed sections */}
                    {shouldShowSectionClaimRow && renderSectionRowData && (
                        <div className="section-claim-row-container bg-white border-bottom p-0">
                            <div className="preview-budget-table">
                                <div className="table-responsive">
                                    <Table className="table mb-0">
                                        <thead>
                                            <tr>
                                                <th style={{ width: '50px' }}></th>
                                                <th>Section</th>
                                                <th>Cost</th>
                                                <th>Completed %</th>
                                                <th>Remaining Amount</th>
                                                <th>Claim %</th>
                                                <th>Claim Amount ($)</th>
                                                <th>Claim Remaining</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {renderSectionRowData(stage, true)}
                                        </tbody>
                                    </Table>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Render nested stages (normal) */}
                    {hasNestedStages && (
                        stage.stages.map((nestedStage) => (
                            <StageAccordionGenerateInvoice
                                key={nestedStage.id}
                                stage={nestedStage}
                                optionData={optionData}
                                renderRowData={renderRowData}
                                renderSectionRowData={renderSectionRowData}
                                stageSummaries={stageSummaries}
                                selectedTask={selectedTask}
                                onSectionClaimChange={onSectionClaimChange}
                                progressClaims={progressClaims}
                                progressClaimId={progressClaimId}
                            />
                        ))
                    )}

                    {/* Render tasks/variations (normal sections only) */}
                    {!isAttributionLevel && !hasNestedStages && (
                        <div className="col-12">
                            <PreviewBudgetTable
                                header="Tasks"
                                tbodyData={stage}
                                renderRowData={renderRowData}
                            />
                            {variationData && Array.isArray(variationData) && variationData.length ? (
                                <PreviewBudgetTable
                                    header="Variations"
                                    tbodyData={variationData}
                                    renderRowData={renderRowData}
                                    stageId={stage.id}
                                />
                            ) : null}
                        </div>
                    )}
                </AccordionBody>
            </AccordionItem>
        </Accordion>
    );
}

export default StageAccordionGenerateInvoice;
